import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Bell, Check, CheckCircle2, Clock, FileText, GraduationCap,
  Home, PlayCircle, Search, ShieldCheck, Smartphone, Sparkles,
  TrendingUp, Trophy, User,
} from 'lucide-react';
import { CTAPair, Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /for-students — "A day in your Kollez app" format.
 *
 * Every other marketing page has its own shape. /for-students is a
 * time-of-day walkthrough: hero shows a mock phone home screen, then
 * six timestamped scenes (8:00 am chat, 9:30 am new drive, 12:30 pm
 * practice, 5:00 pm resume tailoring, this week badges, and the
 * anti-scam "never" commitment). Reads like a scripted product tour
 * rather than a features grid.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (for-students gets the day-in-the-life format).
 *
 * Mobile-first: the phone-mocks are the hero. Sticky bottom CTA
 * preserved from prior iteration.
 */

interface Scene {
  when: string;
  title: string;
  intro: string;
  mock: React.ReactNode;
  bullets: string[];
  cta?: { label: string; to: string };
}

/* ────────── phone-shaped mocks ────────── */

function PhoneFrame({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div
      className="mx-auto"
      style={{
        maxWidth: 320,
        width: '100%',
        borderRadius: 32,
        padding: 12,
        background: 'rgb(var(--ink-primary))',
        boxShadow: '0 20px 40px -20px rgba(0,0,0,0.35)',
      }}
      role="img"
      aria-label={label ?? 'Phone mock-up'}
    >
      <div
        style={{
          borderRadius: 24,
          overflow: 'hidden',
          background: 'rgb(var(--bg-primary))',
          minHeight: 520,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PhoneStatusBar({ time }: { time: string }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-2 text-[11px] font-medium"
      style={{ color: 'rgb(var(--ink-tertiary))' }}
    >
      <span>{time}</span>
      <span className="flex items-center gap-1">
        <span style={{ width: 4, height: 4, background: 'currentColor', borderRadius: 999 }} />
        <span style={{ width: 4, height: 4, background: 'currentColor', borderRadius: 999 }} />
        <span style={{ width: 4, height: 4, background: 'currentColor', borderRadius: 999 }} />
        <span style={{ width: 6, height: 4, background: 'currentColor', borderRadius: 2, marginLeft: 4 }} />
      </span>
    </div>
  );
}

function HomeScreenMock() {
  return (
    <PhoneFrame label="Student home screen at 8 am">
      <PhoneStatusBar time="8:00" />
      <div className="px-4 pb-4 grid gap-3">
        <div className="flex items-center gap-2">
          <span
            className="flex items-center justify-center rounded-full font-semibold text-xs"
            style={{ width: 36, height: 36, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
          >
            MV
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>Monday</div>
            <div className="text-sm font-medium">Hi Meghana</div>
          </div>
          <Bell size={16} style={{ color: 'rgb(var(--ink-tertiary))' }} />
        </div>

        <div
          className="rounded-lg p-3 grid gap-1"
          style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
        >
          <div className="flex items-center gap-1.5 text-[10px] font-medium" style={{ color: 'rgb(var(--ink-accent))' }}>
            <Sparkles size={10} /> NEXIE
          </div>
          <div className="text-xs">
            You've got 2 minutes? Let's finish your SQL practice — 3 questions left.
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-wider" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Eligible today
        </div>
        {[
          { logo: 'W', title: 'Wipro NTH · Project Engineer', chip: 'Day 1', chipTone: 'accent' },
          { logo: 'Z', title: 'Zoho · Backend intern', chip: 'New', chipTone: 'success' },
          { logo: 'F', title: 'Freshworks · SDE 1', chip: '2 days left', chipTone: 'warning' },
        ].map((o) => (
          <div key={o.title} className="rounded-lg p-2.5 flex items-center gap-2.5 card">
            <span
              className="flex items-center justify-center rounded-md text-xs font-semibold flex-shrink-0"
              style={{ width: 32, height: 32, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
            >
              {o.logo}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{o.title}</div>
              <div className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>You're eligible</div>
            </div>
            <span
              className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
              style={{
                background: o.chipTone === 'accent' ? 'rgb(var(--bg-info))' : o.chipTone === 'success' ? 'rgb(var(--bg-success))' : 'rgb(var(--bg-warning))',
                color: o.chipTone === 'accent' ? 'rgb(var(--ink-accent))' : o.chipTone === 'success' ? 'rgb(var(--ink-success))' : 'rgb(var(--ink-warning))',
              }}
            >
              {o.chip}
            </span>
          </div>
        ))}

        <div className="grid grid-cols-4 gap-2 mt-1">
          {[
            { icon: <Home size={14} />, label: 'Home', active: true },
            { icon: <Search size={14} />, label: 'Explore', active: false },
            { icon: <Sparkles size={14} />, label: 'Nexie', active: false },
            { icon: <User size={14} />, label: 'Profile', active: false },
          ].map((t) => (
            <div key={t.label} className="grid gap-1 justify-items-center py-1">
              <span
                style={{
                  color: t.active ? 'rgb(var(--ink-accent))' : 'rgb(var(--ink-tertiary))',
                }}
              >
                {t.icon}
              </span>
              <span
                className="text-[9px]"
                style={{
                  color: t.active ? 'rgb(var(--ink-accent))' : 'rgb(var(--ink-tertiary))',
                  fontWeight: t.active ? 600 : 400,
                }}
              >
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </PhoneFrame>
  );
}

function NexieChatMock() {
  return (
    <PhoneFrame label="Nexie chat at 8 am">
      <PhoneStatusBar time="8:04" />
      <div className="px-4 pb-4 grid gap-3">
        <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
          <span
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
          >
            <Sparkles size={12} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium">Nexie</div>
            <div className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>Career Advisor</div>
          </div>
        </div>

        <div className="rounded-2xl rounded-bl-sm px-3 py-2 text-xs max-w-[92%]" style={{ background: 'rgb(var(--bg-secondary))' }}>
          Your Zoho application went in on Friday. Want to prep for the SQL round they usually run first?
        </div>

        <div className="rounded-2xl rounded-br-sm px-3 py-2 text-xs self-end max-w-[80%]" style={{ background: 'rgb(var(--bg-tertiary))' }}>
          Yes but only 10 min today
        </div>

        <div className="rounded-2xl rounded-bl-sm px-3 py-2.5 text-xs max-w-[92%]" style={{ background: 'rgb(var(--bg-info))' }}>
          <p className="m-0">Perfect. Here's a 10-min set focused on where you struggled last time:</p>
          <ol className="m-0 mt-2 pl-4 grid gap-1">
            <li>JOIN vs subquery — 3 Qs</li>
            <li>Window functions — 4 Qs</li>
            <li>Aggregation edge cases — 3 Qs</li>
          </ol>
          <div className="mt-2 text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            Cited from: your last 2 attempts · Zoho\'s prior drive pattern
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function DriveCardMock() {
  return (
    <PhoneFrame label="New drive notification">
      <PhoneStatusBar time="9:32" />
      <div className="px-4 pb-4 grid gap-3">
        <div
          className="rounded-lg p-2.5 flex items-center gap-2"
          style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
        >
          <Bell size={12} style={{ color: 'rgb(var(--ink-accent))' }} />
          <span className="text-[10px] font-medium">New drive · Day 1 tier · you're eligible</span>
        </div>

        <div className="rounded-lg overflow-hidden card">
          <div className="p-3 flex items-center gap-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
            <span
              className="flex items-center justify-center rounded-md text-sm font-semibold"
              style={{ width: 40, height: 40, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
            >
              W
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium leading-tight">Wipro NTH</div>
              <div className="text-[11px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>Project Engineer</div>
            </div>
          </div>
          <div className="p-3 grid gap-2 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>Drive date</div>
                <div className="font-medium">24 Sept 2026</div>
              </div>
              <div>
                <div className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>Package (illustrative)</div>
                <div className="font-medium">₹6.5 LPA</div>
              </div>
            </div>
            <div
              className="rounded p-2 text-[10px]"
              style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
            >
              <div className="flex items-center gap-1 font-medium">
                <CheckCircle2 size={10} /> You're eligible
              </div>
              <div className="mt-0.5" style={{ color: 'rgb(var(--ink-primary))' }}>
                CGPA 8.7 ≥ 7.5 · CSE ✓ · No backlogs ✓
              </div>
            </div>
            <button
              className="btn btn-primary text-xs w-full mt-1"
              style={{ padding: '0.5rem 0.75rem' }}
            >
              Apply in 3 taps
            </button>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function PracticeMock() {
  const [picked, setPicked] = useState<number | null>(null);
  const options = ['SELECT s FROM x', 'A subquery', 'A window function', 'A CTE'];
  const correct = 2;

  return (
    <PhoneFrame label="Practice question at 12:30">
      <PhoneStatusBar time="12:30" />
      <div className="px-4 pb-4 grid gap-3">
        <div className="flex items-center justify-between text-[10px]">
          <span style={{ color: 'rgb(var(--ink-tertiary))' }}>SQL · Practice · Q 1 of 3</span>
          <span
            className="px-1.5 py-0.5 rounded"
            style={{ background: 'rgb(var(--bg-warning))', color: 'rgb(var(--ink-warning))', fontWeight: 600 }}
          >
            MEDIUM
          </span>
        </div>

        <p className="font-serif text-sm leading-snug m-0">
          To rank employees by salary within each department without collapsing rows,
          which SQL feature would you use?
        </p>

        <div className="grid gap-1.5 text-xs">
          {options.map((o, i) => {
            const isPicked = picked === i;
            const isCorrect = picked !== null && i === correct;
            const isWrong = isPicked && i !== correct;
            return (
              <button
                key={o}
                type="button"
                className="rounded p-2 text-left border flex items-center gap-2"
                style={{
                  borderColor:
                    isCorrect ? 'rgb(var(--ink-success))' :
                    isWrong   ? 'rgb(var(--ink-danger))' :
                    isPicked  ? 'rgb(var(--ink-accent))' :
                                'rgb(var(--line-secondary))',
                  background:
                    isCorrect ? 'rgb(var(--bg-success))' :
                    isWrong   ? 'rgb(var(--bg-danger))' :
                                'rgb(var(--bg-primary))',
                  cursor: 'pointer',
                }}
                onClick={() => setPicked(i)}
                aria-pressed={isPicked}
              >
                <span
                  className="inline-flex items-center justify-center rounded text-[10px] font-semibold flex-shrink-0"
                  style={{
                    width: 18, height: 18,
                    background:
                      isCorrect ? 'rgb(var(--ink-success))' :
                      isWrong   ? 'rgb(var(--ink-danger))' :
                                  'rgb(var(--bg-tertiary))',
                    color:
                      (isCorrect || isWrong) ? 'rgb(var(--ink-inverse))' :
                                                'rgb(var(--ink-primary))',
                  }}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                {o}
              </button>
            );
          })}
        </div>

        {picked !== null && (
          <div
            className="rounded p-2 text-[10px]"
            style={{
              background: picked === correct ? 'rgb(var(--bg-success))' : 'rgb(var(--bg-info))',
              color: 'rgb(var(--ink-primary))',
            }}
          >
            <div className="font-medium mb-1">
              {picked === correct ? 'Correct.' : 'Not quite.'}
            </div>
            Window functions rank without collapsing rows. Subqueries and CTEs
            can express the query but usually with an aggregation that hides rows.
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}

function ResumeMock() {
  return (
    <PhoneFrame label="Resume tailored at 5 pm">
      <PhoneStatusBar time="17:03" />
      <div className="px-4 pb-4 grid gap-3">
        <div className="flex items-center gap-1.5 text-[10px] font-medium" style={{ color: 'rgb(var(--ink-accent))' }}>
          <Sparkles size={10} /> NEXIE · RESUME BUILDER
        </div>
        <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Tailored for <b style={{ color: 'rgb(var(--ink-primary))' }}>Wipro NTH · Project Engineer</b>
        </div>

        <div className="rounded-lg overflow-hidden card">
          <div className="p-3 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
            <div className="font-serif text-sm">Meghana Varma</div>
            <div className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>
              B.Tech CSE · 2026 · Godavari Institute
            </div>
          </div>
          <div className="p-3 grid gap-2 text-[11px]">
            <div>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: 'rgb(var(--ink-tertiary))' }}>
                Highlights (rewritten for this role)
              </div>
              <ul className="mt-1 pl-4 grid gap-1">
                <li>Built ETL pipeline for 2M rows/day (Airflow + BigQuery)</li>
                <li>Cloud Practitioner certified · AWS core</li>
                <li>SQL score 9.4 · System Design L2 · Python verified</li>
              </ul>
            </div>
            <div
              className="rounded p-2 text-[10px]"
              style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-primary))' }}
            >
              <div className="flex items-center gap-1 font-medium" style={{ color: 'rgb(var(--ink-success))' }}>
                <CheckCircle2 size={10} /> ATS-checked — 96 / 100
              </div>
            </div>
            <button
              className="btn btn-primary text-xs w-full"
              style={{ padding: '0.5rem 0.75rem' }}
            >
              Send with your application
            </button>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function BadgeCollectionMock() {
  const BADGES = [
    { label: 'SQL',        level: 'L3', tier: 'Kollez', color: 'green' },
    { label: 'Python',     level: 'L2', tier: 'Kollez', color: 'green' },
    { label: 'System Design', level: 'L2', tier: 'Kollez', color: 'green' },
    { label: 'Cloud Practitioner', level: 'L1', tier: 'AWS', color: 'blue' },
    { label: 'English',    level: 'L3', tier: 'Kollez', color: 'green' },
    { label: 'Cognitive Aptitude', level: 'L3', tier: 'Kollez', color: 'green' },
  ];
  return (
    <PhoneFrame label="Badges earned this week">
      <PhoneStatusBar time="Fri" />
      <div className="px-4 pb-4 grid gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-medium" style={{ color: 'rgb(var(--ink-accent))' }}>
            <Trophy size={10} /> BADGES · YOURS FOR LIFE
          </div>
          <div className="text-sm mt-1">
            <b>6 active</b> · 2 earned this week
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {BADGES.map((b) => (
            <div
              key={b.label}
              className="rounded-lg p-2 grid gap-1 card"
              style={{ borderColor: 'rgb(var(--line-secondary))' }}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="rounded flex items-center justify-center text-[9px] font-semibold"
                  style={{
                    width: 22, height: 22,
                    background: b.color === 'green' ? 'rgb(var(--bg-success))' : 'rgb(var(--bg-info))',
                    color: b.color === 'green' ? 'rgb(var(--ink-success))' : 'rgb(var(--ink-accent))',
                  }}
                >
                  {b.level}
                </span>
                <div className="text-[10px] font-medium leading-tight">{b.label}</div>
              </div>
              <div className="text-[9px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>
                Issued by {b.tier}
              </div>
            </div>
          ))}
        </div>
        <div
          className="rounded p-2 text-[10px]"
          style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
        >
          Every badge follows you — next year, next drive, next opportunity. Skip the retake.
        </div>
      </div>
    </PhoneFrame>
  );
}

/* ────────── scenes ────────── */

const SCENES: Scene[] = [
  {
    when: '8:00 am · Monday',
    title: 'You open your app. Nexie already read the room.',
    intro:
      'Nexie noticed you applied to Zoho on Friday. It suggests a 10-minute SQL warm-up because Zoho\'s first round usually starts there. No login-and-hunt; the next thing to do is on the home screen.',
    mock: <NexieChatMock />,
    bullets: [
      'Chat asks follow-ups grounded in your last answer',
      'Cited from your own past attempts + the drive\'s prior pattern',
      'Skip when you\'re busy — Nexie will ask again later',
    ],
  },
  {
    when: '9:30 am · in class',
    title: 'A Wipro drive lands in your feed. And you\'re actually eligible.',
    intro:
      'Kollez only surfaces drives you\'re eligible for — and shows you why. CGPA, branch, backlogs, backlog policy — all checked against your DigiLocker-verified profile before you see the drive.',
    mock: <DriveCardMock />,
    bullets: [
      'Eligibility check before every drive — reasons visible',
      'DigiLocker-verified marks; no manual proof-uploading',
      'Apply in three taps; the resume is already attached',
    ],
  },
  {
    when: '12:30 pm · lunch break',
    title: 'Three questions of practice. Nexie explains every wrong.',
    intro:
      'Practice mode is not a test — it\'s adaptive difficulty with instant explanations. Every wrong answer surfaces the correct one plus a paragraph of why. Every pass earns a badge you own for life.',
    mock: <PracticeMock />,
    bullets: [
      'Adaptive difficulty per skill',
      'Instant explanations — no waiting for a report',
      'Portable badges recognised by every partner recruiter',
    ],
    cta: { label: 'See all six assessment modes', to: '/assessments' },
  },
  {
    when: '5:00 pm · after college',
    title: 'Nexie rewrote your resume for the drive.',
    intro:
      'The Wipro NTH role has different keywords than your Zoho application. Nexie rewrote the bullets to match, ATS-checked the format, and kept your original resume in the version history if you want to roll back.',
    mock: <ResumeMock />,
    bullets: [
      'Rewritten per role — bullets tuned to the JD',
      'ATS score visible before you send',
      'Every version saved; roll back with one tap',
    ],
  },
  {
    when: 'Friday evening · this week',
    title: 'You\'ve earned 2 more badges. They follow you for life.',
    intro:
      'Every graded assessment you pass earns a Badge — owned by you, not by the drive or the college. Next year\'s campus, next employer\'s test, next skills gate: your badges satisfy them automatically. Skip the retake.',
    mock: <BadgeCollectionMock />,
    bullets: [
      'Six taxonomies: skill · cognitive · real-work · language · soft-skill · domain',
      'Four issuer tiers (Kollez / trusted / standard / self-reported) — visible',
      'Consent-controlled: you decide who sees which badge',
    ],
    cta: { label: 'Explore portable badges', to: '/assessments#badges' },
  },
];

/* ────────── page sub-components ────────── */

function SceneRow({ scene, index }: { scene: Scene; index: number }) {
  const mockLeft = index % 2 === 0;
  return (
    <section
      className="mx-auto max-w-page-wide px-5 lg:px-10 py-14 lg:py-20 grid gap-10 lg:grid-cols-2 lg:gap-16 items-center"
    >
      <div className={mockLeft ? 'lg:order-1' : 'lg:order-2'}>
        {scene.mock}
      </div>
      <div className={`grid gap-4 ${mockLeft ? 'lg:order-2' : 'lg:order-1'}`}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="tabular-nums text-xs flex items-center gap-1"
            style={{ color: 'rgb(var(--ink-accent))' }}
          >
            <Clock size={10} /> {scene.when}
          </span>
        </div>
        <h2
          className="text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            lineHeight: 1.15,
            fontWeight: 500,
            letterSpacing: '-0.005em',
            margin: 0,
          }}
        >
          {scene.title}
        </h2>
        <p className="text-ink-secondary m-0" style={{ maxWidth: '52ch' }}>{scene.intro}</p>
        <ul className="grid gap-2 text-sm text-ink-secondary mt-2">
          {scene.bullets.map((b) => (
            <li key={b} className="flex gap-2">
              <Check size={14} className="mt-1 flex-shrink-0" style={{ color: 'rgb(var(--ink-success))' }} />
              {b}
            </li>
          ))}
        </ul>
        {scene.cta && (
          <Link to={scene.cta.to} className="link text-sm inline-flex items-center gap-1 mt-1">
            {scene.cta.label} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </section>
  );
}

/* ────────── page ────────── */

export function ForStudentsPage() {
  return (
    <>
      <PageMeta
        title="For students"
        description="A day in your Kollez app — 8 am Nexie chat, 9:30 am a Wipro drive lands, lunch break practice, 5 pm resume tailored, Friday badges earned. Free forever. Never a fee."
        canonical="/for-students"
      />

      {/* HERO — phone-mock hero, no side aside */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16 items-center">
        <div className="grid gap-5">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            For students · a day in the app
          </p>
          <h1
            className="text-ink-primary"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            One app. One AI.<br />
            Every drive you can win.
          </h1>
          <p className="text-lg leading-8 text-ink-secondary m-0" style={{ maxWidth: '48ch' }}>
            Kollez lives on your phone. Nexie — our education-tuned AI — is
            the coach in your pocket. Below is a real Monday, hour by hour.
            Free forever. Never a fee.
          </p>
          <div className="mt-2">
            <CTAPair
              primary={{ label: 'Create free profile', to: '/register/student' }}
              secondary={{ label: 'See a day in the app ↓', to: '#scenes' }}
            />
          </div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-tertiary mt-2" aria-label="Student promises">
            <li className="flex items-center gap-1"><Smartphone size={12} className="text-ink-accent" />Mobile-first, works offline</li>
            <li className="flex items-center gap-1"><Sparkles size={12} className="text-ink-accent" />Nexie explains every wrong</li>
            <li className="flex items-center gap-1"><ShieldCheck size={12} className="text-ink-success" />Never a fee. Ever.</li>
          </ul>
        </div>

        <div className="grid place-items-center">
          <HomeScreenMock />
        </div>
      </section>

      {/* SCENES ANCHOR */}
      <div id="scenes" />

      {/* SCENES — five timestamped rows */}
      <div className="border-t border-line-tertiary">
        {SCENES.map((s, i) => (
          <div key={s.when} className={i > 0 ? 'border-t border-line-tertiary' : ''}>
            <SceneRow scene={s} index={i} />
          </div>
        ))}
      </div>

      {/* ANTI-SCAM — full-bleed monumental type, centered */}
      <section
        className="border-t border-line-tertiary py-24 lg:py-32"
        style={{ background: 'rgb(var(--bg-info))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 text-center grid gap-6 justify-items-center">
          <p className="eyebrow flex items-center gap-1.5 m-0" style={{ color: 'rgb(var(--ink-accent))' }}>
            <ShieldCheck size={12} />
            One thing that never happens on Kollez
          </p>
          <h2
            className="text-ink-primary"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: 1.02,
              fontWeight: 500,
              letterSpacing: '-0.015em',
              margin: 0,
              maxWidth: '18ch',
            }}
          >
            You are never asked to pay.
          </h2>
          <p className="text-ink-secondary m-0" style={{ maxWidth: '52ch', fontSize: '1.0625rem' }}>
            Not to apply. Not to interview. Not to accept. Not for a
            training badge, not for a mock interview, not for anything.
            If anyone asks — a company, an agent, a person claiming to
            be from us — it&apos;s a scam.
          </p>
          <Link
            to="/contact?topic=fraud"
            className="btn btn-outline mt-4"
          >
            Report a fraud attempt · we act within a day
          </Link>
        </div>
      </section>

      {/* STAT STRIP — five numbers tied to the five scenes */}
      <section className="border-t border-line-tertiary py-16 lg:py-20" style={{ background: 'rgb(var(--bg-primary))' }}>
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow flex items-center gap-1.5">
              <TrendingUp size={12} />
              This week on Kollez
            </p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Five scenes. Five numbers. Same day.
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { n: '8:04 am',  label: 'first Nexie ping', hint: 'Cited from your last attempt' },
              { n: '3 taps',   label: 'to apply to a drive', hint: 'Eligibility pre-checked' },
              { n: '10 min',   label: 'daily practice',    hint: 'Adaptive difficulty' },
              { n: '96 / 100', label: 'ATS score',         hint: 'Nexie-tailored resume' },
              { n: '6',        label: 'active badges',     hint: 'Yours for life' },
            ].map((s) => (
              <div key={s.label} className="grid gap-1">
                <div
                  className="font-serif tabular-nums"
                  style={{
                    fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
                    fontWeight: 500,
                    lineHeight: 1,
                    color: 'rgb(var(--ink-primary))',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {s.n}
                </div>
                <div className="text-sm font-medium mt-1">{s.label}</div>
                <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>{s.hint}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALL — WhatsApp community hidden for now (user request 2026-09-19) */}
      <section className="border-t border-line-tertiary py-16 lg:py-20" style={{ background: 'rgb(var(--bg-secondary))' }}>
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-6 md:grid-cols-[1fr_1fr] items-center">
          <div className="rounded-lg p-6 grid gap-3 items-start card">
            <Smartphone size={22} style={{ color: 'rgb(var(--ink-accent))' }} />
            <h3 className="font-serif text-xl m-0">Kollez on your home screen</h3>
            <p className="text-sm text-ink-secondary m-0">
              Install once from your mobile browser — offline for saved
              opportunities, notifications for drives you&apos;re eligible for.
            </p>
            <div className="flex gap-2 flex-wrap mt-2">
              <span
                className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs"
                style={{ background: 'rgb(var(--bg-secondary))', color: 'rgb(var(--ink-tertiary))' }}
              >
                <PlayCircle size={14} /> Coming to iOS
              </span>
              <span
                className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs"
                style={{ background: 'rgb(var(--bg-secondary))', color: 'rgb(var(--ink-tertiary))' }}
              >
                <PlayCircle size={14} /> Coming to Android
              </span>
            </div>
          </div>
          <div className="grid gap-3 lg:pl-6">
            <p
              className="eyebrow m-0 text-xs"
              style={{ color: 'rgb(var(--ink-accent))' }}
            >
              Why install
            </p>
            <p className="text-ink-secondary m-0" style={{ maxWidth: '44ch' }}>
              Kollez lives on your phone. The PWA gives you push
              notifications for drives you&apos;re eligible for, offline
              access to saved opportunities, and one-tap re-open — no app
              store required.
            </p>
          </div>
        </div>
      </section>

      {/* MICRO-STORIES — three student voices tied to the scenes, not a testimonials grid */}
      <section className="border-t border-line-tertiary py-16 lg:py-20" style={{ background: 'rgb(var(--bg-primary))' }}>
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow">Voices from the app</p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Three students. Three scenes. Own words.
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                initials: 'MV',
                scene: '5:00 pm scene',
                quote: 'I finished the AWS track in six weeks. Two months later, I had an offer letter — and my resume actually made sense to recruiters.',
                name: 'Meghana Varma',
                where: 'B.Tech CSE 2026 · Godavari IT',
                outcome: '₹9 LPA at Freshworks',
              },
              {
                initials: 'HT',
                scene: 'Nexie chat scene',
                quote: 'The mock interviews are the best I have used. Nexie asked follow-ups on my answer, which no other prep platform did.',
                name: 'Harish T.',
                where: 'B.Tech ECE 2025 · Sri Vasavi',
                outcome: 'Placed at Wipro',
              },
              {
                initials: 'AR',
                scene: '9:30 am drive-lands scene',
                quote: 'My college is small. I did not think MNCs would come to us. Turns out I could apply to their public catalog directly — one profile, everywhere.',
                name: 'Aisha Reddy',
                where: "MCA 2026 · St. Ann's",
                outcome: 'Interned at Zoho',
              },
            ].map((s) => (
              <article key={s.name} className="grid gap-3 pt-3" style={{ borderTop: '2px solid rgb(var(--ink-primary))' }}>
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className="text-[10px] uppercase tracking-widest"
                    style={{ color: 'rgb(var(--ink-accent))' }}
                  >
                    {s.scene}
                  </span>
                </div>
                <p
                  className="font-serif italic m-0"
                  style={{ fontSize: '1.0625rem', lineHeight: 1.4, color: 'rgb(var(--ink-primary))', textWrap: 'pretty' as never }}
                >
                  &ldquo;{s.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span
                    className="flex items-center justify-center rounded-full font-semibold text-xs flex-shrink-0"
                    style={{ width: 32, height: 32, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
                  >
                    {s.initials}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-tight">{s.name}</div>
                    <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>{s.where}</div>
                  </div>
                </div>
                <div
                  className="text-xs font-medium mt-1"
                  style={{ color: 'rgb(var(--ink-success))' }}
                >
                  → {s.outcome}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK ANSWERS — inline dl, not accordion cards */}
      <section className="border-t border-line-tertiary py-16 lg:py-20" style={{ background: 'rgb(var(--bg-secondary))' }}>
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8 lg:grid-cols-[0.6fr_1fr]">
          <div>
            <p className="eyebrow">Quick answers</p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Six things students ask before signing up.
            </h2>
          </div>
          <dl className="grid gap-6">
            {[
              { q: 'Is it really free?',
                a: 'Yes. Students never pay on Kollez. Ever.' },
              { q: 'Where is my data stored?',
                a: 'In India, on servers we control. DPDP Act 2023 compliant. Export or delete anytime from Settings.' },
              { q: 'What does Nexie know about me?',
                a: 'Only what you upload. Every Nexie answer cites its source. Delete anything at any time.' },
              { q: 'Do colleges see my profile?',
                a: 'Only the parts they need to run drives. Not your Nexie chats or mock-interview transcripts.' },
              { q: 'Can I use it if my college isn\'t on Kollez?',
                a: 'Yes. Free profile, apply to the public catalog. Your college shows up when they join.' },
              { q: 'How do internships work?',
                a: 'Same as jobs — apply, get shortlisted, interview. Each listing tells you if it\'s paid or training-first.' },
            ].map((it) => (
              <div key={it.q} className="grid gap-1 pb-4" style={{ borderBottom: '1px solid rgb(var(--line-tertiary))' }}>
                <dt className="font-serif text-lg" style={{ letterSpacing: '-0.005em' }}>{it.q}</dt>
                <dd className="text-ink-secondary text-sm m-0" style={{ maxWidth: '68ch' }}>{it.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Two minutes to sign up. A year of opportunities to open.
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Free forever. Never a fee. Nexie will be there when you finish.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Create free profile', to: '/register/student' }}
            secondary={{ label: 'Meet Nexie', to: '/ai' }}
            align="center"
          />
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="sticky-action">
        <Link to="/register/student" className="btn btn-primary btn-lg w-full">
          Create free profile — free
        </Link>
      </div>
    </>
  );
}
